<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte';
  import {
    deleteProductCompetitorService,
    listProductCompetitorServices,
    patchProductCompetitorService,
  } from '@shared/api/products';
  import {
    countUnlinkedProductCompetitors,
    isUnlinkedProductCompetitor,
    productCompetitorDisplayName,
  } from '@domain/competitor-service/product-competitors-edit';
  import type { ProductCompetitorService } from '@shared/types/product-competitor-service';
  import AddProductCompetitorModal from './AddProductCompetitorModal.svelte';

  interface Props {
    productId: string;
    canEdit?: boolean;
  }

  const { productId, canEdit = false }: Props = $props();

  const AUTO_SAVE_DELAY_MS = 600;
  const UNTITLED_LABEL = '無題の競合ソリューション';
  const MEMO_PLACEHOLDER = 'メモを入力';

  let competitors = $state<ProductCompetitorService[]>([]);
  let memoDrafts = $state<Record<string, string>>({});
  let savingIds = $state<Record<string, boolean>>({});
  let deletingIds = $state<Record<string, boolean>>({});
  let saveErrors = $state<Record<string, string>>({});
  let loading = $state(false);
  let hasLoaded = $state(false);
  let errorMessage = $state<string | null>(null);
  let openMenuId = $state<string | null>(null);
  let addModalOpen = $state(false);
  let addButton = $state<HTMLButtonElement | undefined>(undefined);

  let unlinkedCount = $derived(countUnlinkedProductCompetitors(competitors));

  const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const pendingAfterSave = new Set<string>();
  const lastSavedMemo = new Map<string, string>();

  function memoFromRow(row: ProductCompetitorService): string {
    return row.memo ?? '';
  }

  function currentMemo(id: string): string {
    return memoDrafts[id] ?? '';
  }

  function requestValue(value: string): string | null {
    return value.length === 0 ? null : value;
  }

  function displayTitle(row: ProductCompetitorService): string {
    return productCompetitorDisplayName(row) || UNTITLED_LABEL;
  }

  function syncDrafts(rows: readonly ProductCompetitorService[]): void {
    const nextDrafts: Record<string, string> = {};
    lastSavedMemo.clear();
    for (const row of rows) {
      const memo = memoFromRow(row);
      nextDrafts[row.id] = memo;
      lastSavedMemo.set(row.id, memo);
    }
    memoDrafts = nextDrafts;
    saveErrors = {};
  }

  /**
   * Apply a relation-list refresh without touching drafts or timers for rows
   * the user is already editing. A 409 refresh exists only to make the linked
   * master IDs current; it must not discard unrelated in-progress memo work.
   */
  function reconcileRows(rows: readonly ProductCompetitorService[]): void {
    const nextIds = new Set(rows.map((row) => row.id));
    const nextDrafts: Record<string, string> = {};
    const nextErrors: Record<string, string> = {};

    for (const row of rows) {
      const hasLocalDraft = Object.prototype.hasOwnProperty.call(
        memoDrafts,
        row.id,
      );
      const preserveLocalDraft =
        hasLocalDraft && (hasUnsavedMemo(row.id) || isSaving(row.id));
      if (preserveLocalDraft) {
        nextDrafts[row.id] = currentMemo(row.id);
        const rowError = saveErrors[row.id];
        if (rowError) nextErrors[row.id] = rowError;
      } else {
        const memo = memoFromRow(row);
        nextDrafts[row.id] = memo;
        lastSavedMemo.set(row.id, memo);
      }
    }

    for (const row of competitors) {
      if (nextIds.has(row.id)) continue;
      clearSaveTimer(row.id);
      pendingAfterSave.delete(row.id);
      lastSavedMemo.delete(row.id);
    }

    competitors = [...rows];
    memoDrafts = nextDrafts;
    saveErrors = nextErrors;
    savingIds = Object.fromEntries(
      Object.entries(savingIds).filter(([id]) => nextIds.has(id)),
    );
    deletingIds = Object.fromEntries(
      Object.entries(deletingIds).filter(([id]) => nextIds.has(id)),
    );
    if (openMenuId && !nextIds.has(openMenuId)) openMenuId = null;
  }

  function setFlag(
    record: Record<string, boolean>,
    id: string,
    value: boolean,
  ): Record<string, boolean> {
    const next = { ...record };
    if (value) next[id] = true;
    else delete next[id];
    return next;
  }

  function setSaving(id: string, value: boolean): void {
    savingIds = setFlag(savingIds, id, value);
  }

  function setDeleting(id: string, value: boolean): void {
    deletingIds = setFlag(deletingIds, id, value);
  }

  function isSaving(id: string): boolean {
    return savingIds[id] === true;
  }

  function isDeleting(id: string): boolean {
    return deletingIds[id] === true;
  }

  function setRowError(id: string, message: string | null): void {
    const next = { ...saveErrors };
    if (message) next[id] = message;
    else delete next[id];
    saveErrors = next;
  }

  function clearSaveTimer(id: string): void {
    const timer = saveTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      saveTimers.delete(id);
    }
  }

  function clearAllSaveTimers(): void {
    for (const timer of saveTimers.values()) clearTimeout(timer);
    saveTimers.clear();
  }

  function hasUnsavedMemo(id: string): boolean {
    return lastSavedMemo.get(id) !== currentMemo(id);
  }

  function scheduleSave(id: string, delay = AUTO_SAVE_DELAY_MS): void {
    if (!canEdit) return;
    clearSaveTimer(id);
    saveTimers.set(
      id,
      setTimeout(() => {
        saveTimers.delete(id);
        void flushSave(id);
      }, delay),
    );
  }

  function updateMemo(id: string, value: string): void {
    memoDrafts = { ...memoDrafts, [id]: value };
    setRowError(id, null);
    scheduleSave(id);
  }

  async function flushSave(id: string): Promise<void> {
    if (!canEdit) return;
    clearSaveTimer(id);
    if (isDeleting(id) || !hasUnsavedMemo(id)) return;
    if (isSaving(id)) {
      pendingAfterSave.add(id);
      return;
    }

    const sentMemo = currentMemo(id);
    setSaving(id, true);
    setRowError(id, null);
    let saved = false;
    try {
      const updated = await patchProductCompetitorService(productId, id, {
        memo: requestValue(sentMemo),
      });
      if (!competitors.some((item) => item.id === id)) {
        saved = true;
        return;
      }
      competitors = competitors.map((item) =>
        item.id === updated.id ? updated : item,
      );
      const savedMemo = memoFromRow(updated);
      lastSavedMemo.set(id, savedMemo);
      if (currentMemo(id) === sentMemo) {
        memoDrafts = { ...memoDrafts, [id]: savedMemo };
      }
      saved = true;
    } catch (error) {
      setRowError(
        id,
        error instanceof Error
          ? error.message
          : '保存に失敗しました。入力内容は保持しています。',
      );
    } finally {
      setSaving(id, false);
      const needsAnotherSave =
        pendingAfterSave.delete(id) || hasUnsavedMemo(id);
      if (saved && needsAnotherSave) scheduleSave(id, 0);
    }
  }

  async function load(): Promise<void> {
    if (!productId) return;
    loading = true;
    hasLoaded = false;
    errorMessage = null;
    clearAllSaveTimers();
    try {
      const response = await listProductCompetitorServices(productId);
      competitors = response.items;
      syncDrafts(response.items);
      hasLoaded = true;
    } catch (error) {
      errorMessage =
        error instanceof Error
          ? error.message
          : '競合ソリューションの読み込みに失敗しました';
    } finally {
      loading = false;
    }
  }

  async function refreshPreservingDrafts(): Promise<boolean> {
    if (!productId) return false;
    loading = true;
    hasLoaded = false;
    errorMessage = null;
    try {
      const response = await listProductCompetitorServices(productId);
      reconcileRows(response.items);
      hasLoaded = true;
      return true;
    } catch (error) {
      errorMessage =
        error instanceof Error
          ? error.message
          : '競合ソリューションの読み込みに失敗しました';
      return false;
    } finally {
      loading = false;
    }
  }

  function openAddModal(): void {
    if (!canEdit || loading || !hasLoaded || errorMessage !== null) return;
    addModalOpen = true;
  }

  async function closeAddModal(): Promise<void> {
    addModalOpen = false;
    await tick();
    addButton?.focus();
  }

  async function handleCreated(row: ProductCompetitorService): Promise<void> {
    competitors = [...competitors, row];
    const memo = memoFromRow(row);
    memoDrafts = { ...memoDrafts, [row.id]: memo };
    lastSavedMemo.set(row.id, memo);
    await closeAddModal();
  }

  async function handleConflict(): Promise<void> {
    if (!(await refreshPreservingDrafts())) await closeAddModal();
  }

  function removeLocalRow(id: string): void {
    clearSaveTimer(id);
    pendingAfterSave.delete(id);
    lastSavedMemo.delete(id);
    const nextDrafts = { ...memoDrafts };
    delete nextDrafts[id];
    memoDrafts = nextDrafts;
    setSaving(id, false);
    setDeleting(id, false);
    setRowError(id, null);
  }

  async function deleteCompetitor(
    row: ProductCompetitorService,
  ): Promise<void> {
    if (!canEdit || isDeleting(row.id) || isSaving(row.id)) return;
    const title = displayTitle(row);
    const confirmation = isUnlinkedProductCompetitor(row)
      ? `未紐付けカード「${title}」を削除しますか？`
      : `「${title}」との紐付けを解除しますか？\nこのカードに記入したメモは削除されます。\n外部ソリューション本体と顧客側の利用情報は削除されません。`;
    if (!confirm(confirmation)) return;

    openMenuId = null;
    setDeleting(row.id, true);
    setRowError(row.id, null);
    try {
      await deleteProductCompetitorService(productId, row.id);
      competitors = competitors.filter((item) => item.id !== row.id);
      removeLocalRow(row.id);
    } catch (error) {
      setRowError(
        row.id,
        error instanceof Error
          ? error.message
          : '削除に失敗しました。再試行してください。',
      );
      setDeleting(row.id, false);
    }
  }

  function toggleMenu(id: string): void {
    openMenuId = openMenuId === id ? null : id;
  }

  function inputValue(event: Event): string {
    return (event.currentTarget as HTMLTextAreaElement).value;
  }

  onMount(() => {
    void load();
  });

  onDestroy(() => {
    clearAllSaveTimers();
  });
</script>

<section
  class="competitors-editor"
  data-testid="product-competitors-editor"
  aria-label="競合ソリューション"
>
  <header class="editor-header">
    <div class="heading-stack">
      <h3>競合ソリューション</h3>
      {#if hasLoaded}
        <span
          class="unlinked-count"
          class:warning={unlinkedCount > 0}
          data-testid="product-competitors-unlinked-count"
        >
          未紐付け {unlinkedCount}件
        </span>
      {/if}
    </div>
    <div class="header-actions">
      {#if loading}<span class="muted small">読み込み中...</span>{/if}
      {#if canEdit}
        <button
          bind:this={addButton}
          type="button"
          class="btn primary"
          data-testid="product-competitors-add"
          disabled={loading || !hasLoaded || errorMessage !== null}
          onclick={openAddModal}
        >
          追加
        </button>
      {/if}
    </div>
  </header>

  {#if errorMessage}
    <div class="load-error" role="alert">
      <p class="error" data-testid="product-competitors-error">
        {errorMessage}
      </p>
      <button
        type="button"
        class="btn"
        data-testid="product-competitors-retry"
        disabled={loading}
        onclick={() => void refreshPreservingDrafts()}
      >
        再試行
      </button>
    </div>
  {/if}

  {#if loading && competitors.length === 0}
    <p class="empty" data-testid="product-competitors-loading">
      競合ソリューションを読み込んでいます。
    </p>
  {:else if competitors.length === 0}
    <p class="empty" data-testid="product-competitors-empty">
      まだ競合ソリューションが登録されていません。
    </p>
  {:else}
    <div class="rows" data-testid="product-competitors-list">
      {#each competitors as row (row.id)}
        {@const unlinked = isUnlinkedProductCompetitor(row)}
        {@const title = displayTitle(row)}
        <article
          class="competitor-row"
          class:unlinked
          data-testid="product-competitors-row"
          data-competitor-id={row.id}
        >
          <header class="row-header">
            <div class="title-stack">
              <div class="title-line">
                {#if row.competitor_service_id}
                  <a
                    class="title-link"
                    href={`/third-party-solutions/${row.competitor_service_id}`}
                  >
                    {title}
                  </a>
                {:else}
                  <strong>{title}</strong>
                  <span
                    class="unlinked-badge"
                    data-testid="product-competitors-unlinked-badge"
                  >
                    未紐付け
                  </span>
                {/if}
              </div>
              <span class="usage-count">競合利用 {row.usage_target_count}</span>
            </div>

            {#if canEdit}
              <div class="menu-wrap">
                <button
                  type="button"
                  class="icon-button"
                  aria-label="操作メニュー"
                  aria-haspopup="menu"
                  aria-expanded={openMenuId === row.id}
                  data-testid="product-competitors-menu"
                  disabled={isDeleting(row.id) || isSaving(row.id)}
                  onclick={() => toggleMenu(row.id)}
                >
                  ⋯
                </button>
                {#if openMenuId === row.id}
                  <div class="menu-popover" role="menu">
                    <button
                      type="button"
                      class="menu-item danger"
                      role="menuitem"
                      data-testid="product-competitors-delete"
                      disabled={isDeleting(row.id) || isSaving(row.id)}
                      onclick={() => void deleteCompetitor(row)}
                    >
                      {unlinked ? '削除' : '紐付け解除'}
                    </button>
                  </div>
                {/if}
              </div>
            {/if}
          </header>

          {#if canEdit}
            <textarea
              class="memo-input"
              data-testid="product-competitors-memo-textarea"
              aria-label={`${title}のメモ`}
              placeholder={MEMO_PLACEHOLDER}
              maxlength="2000"
              rows="4"
              value={currentMemo(row.id)}
              disabled={isDeleting(row.id)}
              oninput={(event) => updateMemo(row.id, inputValue(event))}
              onblur={() => void flushSave(row.id)}
            ></textarea>
          {:else if currentMemo(row.id).length > 0}
            <p class="memo-text">{currentMemo(row.id)}</p>
          {:else}
            <p class="memo-placeholder">{MEMO_PLACEHOLDER}</p>
          {/if}

          {#if saveErrors[row.id]}
            <p
              class="row-error"
              data-testid="product-competitors-row-error"
              role="alert"
            >
              {saveErrors[row.id]}
            </p>
          {/if}
        </article>
      {/each}
    </div>
  {/if}
</section>

<AddProductCompetitorModal
  open={addModalOpen}
  {productId}
  existingRows={competitors}
  onClose={() => void closeAddModal()}
  onCreated={(row) => void handleCreated(row)}
  onConflict={handleConflict}
/>

<style>
  .competitors-editor {
    display: grid;
    gap: 14px;
  }
  .editor-header,
  .row-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }
  .heading-stack,
  .header-actions,
  .title-line {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .editor-header h3 {
    margin: 0;
    font-size: 15px;
  }
  .unlinked-count {
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    padding: 3px 8px;
    font-size: 12px;
    font-weight: 700;
  }
  .unlinked-count.warning {
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }
  .rows {
    display: grid;
    gap: 12px;
  }
  .competitor-row {
    display: grid;
    gap: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
    background: #fff;
  }
  .competitor-row.unlinked {
    border-color: #fcd34d;
    background: #fffdf5;
  }
  .title-stack {
    display: grid;
    gap: 3px;
    flex: 1 1 auto;
    min-width: 0;
  }
  .title-stack strong,
  .title-link {
    color: #0f172a;
    font-size: 15px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .title-link {
    color: #1d4ed8;
    text-decoration: none;
  }
  .title-link:hover,
  .title-link:focus {
    text-decoration: underline;
  }
  .unlinked-badge {
    flex: 0 0 auto;
    color: #92400e;
    background: #fef3c7;
    border-radius: 999px;
    padding: 2px 7px;
    font-size: 11px;
    font-weight: 700;
  }
  .usage-count {
    color: #64748b;
    font-size: 12px;
  }
  .memo-input {
    width: 100%;
    min-height: 92px;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 8px 10px;
    color: #1f2937;
    background: #f8fafc;
    font: inherit;
    line-height: 1.5;
    resize: vertical;
  }
  .memo-input:hover:not(:disabled),
  .memo-input:focus {
    border-color: #cbd5e1;
    background: #fff;
    outline: none;
  }
  .memo-input::placeholder,
  .memo-placeholder {
    color: #94a3b8;
  }
  .memo-text,
  .memo-placeholder {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    line-height: 1.55;
  }
  .menu-wrap {
    position: relative;
    flex: 0 0 auto;
  }
  .icon-button {
    display: inline-grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #475569;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
  }
  .icon-button:hover:not(:disabled),
  .icon-button:focus {
    border-color: #cbd5e1;
    background: #f8fafc;
    outline: none;
  }
  .menu-popover {
    position: absolute;
    top: 36px;
    right: 0;
    z-index: 10;
    min-width: 128px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 10px 24px rgb(15 23 42 / 12%);
    padding: 4px;
  }
  .menu-item {
    width: 100%;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #334155;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    padding: 8px 10px;
    text-align: left;
  }
  .menu-item.danger {
    color: #b91c1c;
  }
  .menu-item.danger:hover:not(:disabled) {
    background: #fef2f2;
  }
  .btn {
    border: 1px solid #cbd5e1;
    background: #fff;
    color: #334155;
    border-radius: 6px;
    padding: 7px 12px;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
  }
  .btn.primary {
    border-color: #1d4ed8;
    background: #1d4ed8;
    color: #fff;
  }
  .btn.primary:hover:not(:disabled) {
    background: #1e40af;
  }
  .btn:disabled,
  .icon-button:disabled,
  .menu-item:disabled,
  .memo-input:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .error,
  .row-error {
    margin: 0;
    color: #b91c1c;
    font-size: 12px;
  }
  .load-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .empty,
  .muted {
    margin: 0;
    color: #64748b;
  }
  .small {
    font-size: 12px;
  }
  @media (max-width: 640px) {
    .editor-header,
    .row-header {
      align-items: stretch;
    }
    .editor-header {
      flex-direction: column;
    }
    .header-actions {
      justify-content: space-between;
    }
  }
</style>
